import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import type { SelectOption } from '@/components/molecules'
import { usePets } from '@/shared/hooks/queries/use-pets'
import { usePolicies } from '@/shared/hooks/queries/use-policies'
import { formatAmountCents, toAmountCents } from '@/shared/helpers/amount-cents'
import {
  ACTIVE_POLICY_STATUS,
  PET_CLAIM_DESCRIPTION_MAX_LENGTH,
} from '@/shared/constants/claims'
import type { PetClaimFormValues } from '../page.claims.pet.hook'

export function useClaimsPetStepOne() {
  const { t } = useTranslation()
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<PetClaimFormValues>()

  const { data: pets = [] } = usePets()
  const { data: policies = [] } = usePolicies()

  const petOptions = useMemo<SelectOption[]>(
    () =>
      policies
        .filter((policy) => policy.status === ACTIVE_POLICY_STATUS)
        .map((policy) => pets.find((pet) => pet.id === policy.petId))
        .filter((pet) => pet?.id !== undefined)
        .map((pet) => ({ value: String(pet?.id), label: pet?.name ?? '' })),
    [pets, policies],
  )

  useEffect(() => {
    const [onlyPet] = petOptions
    if (petOptions.length === 1 && onlyPet && !getValues('petId')) {
      setValue('petId', onlyPet.value)
    }
  }, [petOptions, setValue, getValues])

  const requiredRule = { required: t('common.required') }
  const rules = {
    petId: requiredRule,
    // The API takes any invoice date that isn't in the future
    invoiceDate: requiredRule,
    // Required to submit: the insurance core won't file a claim without it
    description: {
      ...requiredRule,
      maxLength: {
        value: PET_CLAIM_DESCRIPTION_MAX_LENGTH,
        message: t('claims.pet.form.descriptionTooLong', {
          max: PET_CLAIM_DESCRIPTION_MAX_LENGTH,
        }),
      },
    },
    amount: {
      ...requiredRule,
      validate: (value: string) =>
        Number(value) > 0 || t('claims.pet.form.amountError'),
    },
  }

  const fieldErrors = {
    petId: errors.petId?.message,
    invoiceDate: errors.invoiceDate?.message,
    description: errors.description?.message,
    amount: errors.amount?.message,
  }

  return {
    control,
    rules,
    fieldErrors,
    petOptions,
    toAmountCents,
    formatAmountCents,
    invoiceMaxDate: new Date(),
    descriptionMaxLength: PET_CLAIM_DESCRIPTION_MAX_LENGTH,
  }
}
